import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { PageHead, Skel, Empty, Stat } from '../components/ui'
import { money, thaiDate } from '../lib/format'

type ContractStatus = 'draft' | 'active' | 'expired' | 'terminated'
type ExpiryState = 'active' | 'expiring_soon' | 'expired'
type AnomalyCode =
  | 'EXPIRED_BUT_PAID'
  | 'VALUE_EXCEEDED'
  | 'MISSING_COUNTERPARTY'
  | 'AUTO_RENEW_NEAR_EXPIRY'
  | 'DUPLICATE_CONTRACT_NO'

interface ContractRow {
  id: string
  contract_no: string
  counterparty_id?: string
  counterparty_name?: string
  title: string
  value_satang: number
  currency: string
  signed_at: string
  expires_at?: string
  auto_renew: boolean
  status: ContractStatus
  created_at: string
  paid_satang: number
  paid_after_expiry_satang: number
  payment_count: number
  last_paid_at?: string
  expiry_state: ExpiryState
}

interface Anomaly {
  code: AnomalyCode
  severity: string
  contract_id: string
  contract_no: string
  counterparty_name?: string
  message: string
  amount_satang?: number
}

const statusLabel: Record<ContractStatus, string> = {
  draft: 'ฉบับร่าง', active: 'มีผล', expired: 'หมดอายุ', terminated: 'ยกเลิก',
}

const expiryLabel: Record<ExpiryState, string> = {
  active: 'มีผลอยู่', expiring_soon: 'ใกล้ครบกำหนด', expired: 'หมดอายุ',
}

const anomalyLabel: Record<AnomalyCode, string> = {
  EXPIRED_BUT_PAID: 'จ่ายเงินหลังสัญญาหมดอายุ',
  VALUE_EXCEEDED: 'จ่ายเกินมูลค่าสัญญาเกิน 10%',
  MISSING_COUNTERPARTY: 'ไม่ระบุคู่สัญญา',
  AUTO_RENEW_NEAR_EXPIRY: 'ต่ออายุอัตโนมัติใกล้ครบกำหนด',
  DUPLICATE_CONTRACT_NO: 'เลขที่สัญญาซ้ำกัน',
}

function ExpiryCell({ c }: { c: ContractRow }) {
  const expired = c.expiry_state === 'expired'
  const soon = c.expiry_state === 'expiring_soon'
  return (
    <div>
      <span
        className={`chip${soon ? ' gold' : ''}`}
        style={expired ? { color: 'var(--color-crimson)', borderColor: 'rgba(166,58,74,0.35)' } : undefined}
      >
        {expiryLabel[c.expiry_state] ?? c.expiry_state}
      </span>
      <div className="dim tnum" style={{ fontSize: 11, marginTop: 4 }}>
        {c.expires_at ? `ถึง ${thaiDate(c.expires_at)}` : 'ไม่มีวันหมดอายุ'}
      </div>
    </div>
  )
}

export function Contracts() {
  const qc = useQueryClient()
  const [composing, setComposing] = useState(false)
  const [contractNo, setContractNo] = useState('')
  const [title, setTitle] = useState('')
  const [counterpartyId, setCounterpartyId] = useState('')
  const [counterpartyName, setCounterpartyName] = useState('')
  const [valueBaht, setValueBaht] = useState('')
  const [signedAt, setSignedAt] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [autoRenew, setAutoRenew] = useState(false)
  const [status, setStatus] = useState<ContractStatus>('active')

  const contracts = useQuery({
    queryKey: ['contracts'],
    queryFn: () => api<ContractRow[]>('/v1/contracts'),
    staleTime: 30_000,
  })
  const anomalies = useQuery({
    queryKey: ['contract-anomalies'],
    queryFn: () => api<Anomaly[]>('/v1/contracts/anomalies'),
    staleTime: 30_000,
  })

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['contracts'] })
    void qc.invalidateQueries({ queryKey: ['contract-anomalies'] })
  }

  const create = useMutation({
    mutationFn: () => apiWrite<ContractRow>('/v1/contracts', 'POST', {
      contract_no: contractNo.trim(),
      counterparty_id: counterpartyId.trim(),
      counterparty_name: counterpartyName.trim(),
      title: title.trim(),
      value_satang: Math.round(Number(valueBaht) * 100),
      currency: 'THB',
      signed_at: signedAt,
      expires_at: expiresAt,
      auto_renew: autoRenew,
      status,
    }),
    onSuccess: () => {
      setComposing(false)
      setContractNo('')
      setTitle('')
      setCounterpartyId('')
      setCounterpartyName('')
      setValueBaht('')
      setSignedAt('')
      setExpiresAt('')
      setAutoRenew(false)
      setStatus('active')
      refresh()
    },
  })

  const rows = contracts.data ?? []
  const found = anomalies.data ?? []
  const expiringSoon = rows.filter((c) => c.expiry_state === 'expiring_soon').length
  const valueSatang = Math.round(Number(valueBaht) * 100)
  const canSave = contractNo.trim().length > 0 && title.trim().length > 0
    && counterpartyId.trim().length > 0 && signedAt.length > 0
    && Number.isFinite(valueSatang) && valueSatang > 0

  return (
    <>
      <PageHead
        title="ทะเบียนสัญญา"
        sub="สัญญาและคู่สัญญาที่ตรวจพบ · เทียบการจ่ายเงินกับมูลค่าและวันหมดอายุของแต่ละฉบับ"
        actions={
          <button className="btn btn-primary" onClick={() => setComposing((v) => !v)}>
            {composing ? 'ยกเลิก' : 'เพิ่มสัญญา'}
          </button>
        }
      />

      <div className="grid-3" style={{ marginBottom: 16 }}>
        <Stat label="สัญญาในทะเบียน" value={rows.length} unit="ฉบับ" foot="นับทุกสถานะ" />
        <Stat
          label="ใกล้ครบกำหนด"
          value={<span style={{ color: expiringSoon > 0 ? 'var(--color-gold)' : undefined }}>{expiringSoon}</span>}
          unit="ฉบับ"
          foot="หมดอายุภายใน 60 วัน"
        />
        <Stat
          label="ข้อผิดปกติ"
          value={<span style={{ color: found.length > 0 ? 'var(--color-crimson)' : undefined }}>{found.length}</span>}
          unit="รายการ"
          foot="ตรวจจากการจ่ายเงินและข้อมูลทะเบียน"
        />
      </div>

      {found.length > 0 && (
        <div className="card card-pad rise" style={{ marginBottom: 16, borderColor: 'rgba(166,58,74,0.32)' }}>
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
            <div className="card-label" style={{ margin: 0 }}>ข้อผิดปกติที่ตรวจพบ</div>
            <span className="sev sev-critical"><i className="sev-dot" />ต้องตรวจสอบก่อนอนุมัติ</span>
          </div>
          {found.map((a, i) => (
            <div
              key={`${a.contract_id}-${a.code}-${i}`}
              className="row"
              style={{ gap: 12, padding: '9px 0', borderTop: i > 0 ? '1px solid var(--color-hairline)' : undefined }}
            >
              <span className="mono dim" style={{ minWidth: 116 }}>{a.contract_no.trim()}</span>
              <span className={`sev sev-${a.severity}`} style={{ minWidth: 230 }}>
                <i className="sev-dot" />{anomalyLabel[a.code] ?? a.code}
              </span>
              <span className="dim" style={{ fontSize: 12 }}>
                {a.counterparty_name?.trim() || 'ไม่ระบุคู่สัญญา'}
                {a.amount_satang ? ` · ${money(a.amount_satang)}` : ''}
              </span>
            </div>
          ))}
        </div>
      )}

      {composing && (
        <div className="card card-pad rise" style={{ marginBottom: 16 }}>
          <div className="grid-3">
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="contract-no">เลขที่สัญญา</label>
              <input id="contract-no" className="input" style={{ width: '100%' }} maxLength={80}
                value={contractNo} onChange={(e) => setContractNo(e.target.value)} placeholder="เช่น CT-2026-015" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="contract-title">ชื่อสัญญา</label>
              <input id="contract-title" className="input" style={{ width: '100%' }} maxLength={240}
                value={title} onChange={(e) => setTitle(e.target.value)} placeholder="เช่น สัญญาจัดหาวัตถุดิบ" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="contract-value">มูลค่า (บาท)</label>
              <input id="contract-value" className="input tnum" style={{ width: '100%' }} type="number" min="0" step="0.01"
                value={valueBaht} onChange={(e) => setValueBaht(e.target.value)} placeholder="0.00" />
            </div>
          </div>
          <div className="grid-3">
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="contract-party-id">รหัสคู่สัญญา</label>
              <input id="contract-party-id" className="input mono" style={{ width: '100%' }} maxLength={64}
                value={counterpartyId} onChange={(e) => setCounterpartyId(e.target.value)} placeholder="เช่น V-7007" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="contract-party-name">ชื่อคู่สัญญา</label>
              <input id="contract-party-name" className="input" style={{ width: '100%' }} maxLength={200}
                value={counterpartyName} onChange={(e) => setCounterpartyName(e.target.value)} placeholder="ชื่อนิติบุคคลหรือบุคคล" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="contract-status">สถานะ</label>
              <select id="contract-status" className="select" style={{ width: '100%' }}
                value={status} onChange={(e) => setStatus(e.target.value as ContractStatus)}>
                <option value="active">มีผล</option>
                <option value="draft">ฉบับร่าง</option>
                <option value="expired">หมดอายุ</option>
                <option value="terminated">ยกเลิก</option>
              </select>
            </div>
          </div>
          <div className="grid-3">
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="contract-signed">วันลงนาม</label>
              <input id="contract-signed" className="input tnum" style={{ width: '100%' }} type="date"
                value={signedAt} onChange={(e) => setSignedAt(e.target.value)} />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="contract-expires">วันหมดอายุ</label>
              <input id="contract-expires" className="input tnum" style={{ width: '100%' }} type="date"
                value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="contract-renew">การต่ออายุ</label>
              <div className="row" style={{ gap: 8, fontSize: 12.5, paddingTop: 8 }}>
                <input id="contract-renew" type="checkbox" checked={autoRenew}
                  onChange={(e) => setAutoRenew(e.target.checked)} />
                <span className="dim">ต่ออายุอัตโนมัติ</span>
              </div>
            </div>
          </div>
          <div className="row" style={{ marginTop: 14, gap: 10 }}>
            <button className="btn btn-primary" disabled={create.isPending || !canSave} onClick={() => create.mutate()}>
              บันทึกสัญญา
            </button>
            {create.error && <span className="sev sev-critical"><i className="sev-dot" />{(create.error as Error).message}</span>}
          </div>
        </div>
      )}

      {contracts.isLoading ? <Skel h={240} /> : rows.length === 0 ? (
        <Empty title="ยังไม่มีสัญญาในทะเบียน" body="เพิ่มสัญญาฉบับแรก แล้วระบบจะเทียบการจ่ายเงินกับมูลค่าและวันหมดอายุให้" />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>เลขที่สัญญา</th><th>คู่สัญญา</th><th>มูลค่า</th><th>วันหมดอายุ</th><th>สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td style={{ maxWidth: 300 }}>
                    <div className="mono">{c.contract_no.trim()}</div>
                    <div className="dim" style={{ fontSize: 11.5, marginTop: 3 }}>{c.title}</div>
                  </td>
                  <td>
                    <div>{c.counterparty_name?.trim() || '—'}</div>
                    <div className="mono dim" style={{ fontSize: 11, marginTop: 3 }}>
                      {c.counterparty_id?.trim() || 'ไม่มีรหัสคู่สัญญา'}
                    </div>
                  </td>
                  <td className="num">
                    {money(c.value_satang)}
                    {c.paid_satang + c.paid_after_expiry_satang > 0 && (
                      <div className="dim tnum" style={{ fontSize: 11, marginTop: 3 }}>
                        จ่ายแล้ว {money(c.paid_satang + c.paid_after_expiry_satang)}
                      </div>
                    )}
                  </td>
                  <td><ExpiryCell c={c} /></td>
                  <td>
                    <span className={`chip${c.status === 'active' ? ' gold' : ''}`}>
                      {statusLabel[c.status] ?? c.status}
                    </span>
                    {c.auto_renew && <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>ต่ออายุอัตโนมัติ</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
